"use client";
import { AppColors } from "@/assets/colors";
import { pxToRem } from "@/util";
import { Box } from "@mui/material";
import React, { ReactNode, useRef, useState } from "react";
import { Swiper, SwiperSlide, SwiperRef } from "swiper/react";
import {  Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()(() => ({
  carouselContainer: {
    width: "100%",
    height: "100%",
    position: 'relative',
    display:'flex',
    justifyContent: "center",
    alignItems: "center"
  },
  carouselItem: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    flex:1
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: pxToRem(8),
    marginTop: pxToRem(16),
    height:pxToRem(20)
  },
  paginationDot: {
    width: pxToRem(16),
    height: pxToRem(16),
    borderRadius: "50%",
    backgroundColor: AppColors.neutral["75"],
    cursor: 'pointer',
    transition: "all 0.3s ease",
    '&.active': {
      backgroundColor: AppColors.primary["300"],
      border: `2px solid ${AppColors.primary["300"]}`,
      transform: "scale(1.2)",
    }
  },
}));

type Props = {
  children: ReactNode[] | ReactNode;
  autoPlaySpeed?: number;
  isPagination?: boolean;
};

export const AboutCarousel: React.FC<Props> = ({
  children,
  autoPlaySpeed = 3000,
  isPagination = true,
}) => {
  const { classes } = useStyles();
  const swiperRef = useRef<SwiperRef>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const slides = Array.isArray(children) ? children : [children];

  const handleDotClick = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slideTo(index);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%',display:'flex',flexDirection:'column' }}>
      <Swiper
        ref={swiperRef}
        modules={[Autoplay]}
        autoplay={{ delay: autoPlaySpeed }}
        loop={true}
        style={{ width: '100%', height: '100%' }}
        className={classes.carouselContainer}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
      >
        {slides.map((child, index) => (
          <SwiperSlide key={index}>
            <Box className={classes.carouselItem}>{child}</Box>
          </SwiperSlide>
        ))}
      </Swiper>

      {isPagination && (
        <Box className={classes.paginationContainer}>
          {slides.map((_, index) => (
            <Box
              key={index}
              className={`${classes.paginationDot} ${activeIndex === index ? 'active' : ''}`}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};